import { useRef } from "react"
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import {
  MEDIA_ACCEPT,
  type MediaUploads,
  type MediaWriteAccess,
} from "../../hooks/useMediaUploads"
import { FeatureErrors } from "../common/featureErrors"

export interface MediaUploadQueueProps extends MediaWriteAccess {
  uploads: MediaUploads
}
const labels = {
  queued: "В очереди",
  uploading: "Загружается",
  success: "Загружен",
  error: "Ошибка",
}
export function MediaUploadQueue({
  uploads,
  readOnly,
  canWrite,
}: MediaUploadQueueProps) {
  const disabled = readOnly || canWrite === false
  const input = useRef<HTMLInputElement>(null)
  // Completion ownership belongs to the hook/picker, not the visible queue.
  const rows = uploads.items.flatMap((item, index) =>
    item.status === "success" ? [] : [{ item, index }],
  )
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button
          variant="contained"
          sx={{ alignSelf: "flex-start" }}
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          Загрузить фото или видео
        </Button>
      </Stack>
      <input
        ref={input}
        aria-label="Загрузить фото или видео"
        type="file"
        hidden
        multiple
        accept={MEDIA_ACCEPT}
        disabled={disabled}
        onChange={(event) => {
          if (!disabled) uploads.addFiles(Array.from(event.target.files ?? []))
          event.target.value = ""
        }}
      />
      <Typography variant="caption">Фото до 5 МБ, видео до 20 МБ.</Typography>
      {rows.length > 0 && (
        <TableContainer>
          <Table
            size="small"
            aria-label="Очередь загрузки"
            sx={{
              "& .MuiTableCell-root": {
                borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
              },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Файл",
                  "Размер",
                  "Состояние",
                  "Предпросмотр",
                  "Действия",
                ].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ item, index }) => (
                <TableRow key={item.localId}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ minWidth: 140, maxWidth: 260 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {item.file.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {item.file.size < 1024
                      ? `${item.file.size} Б`
                      : item.file.size < 1024 * 1024
                        ? `${Number((item.file.size / 1024).toFixed(1))} КБ`
                        : `${Number((item.file.size / 1024 / 1024).toFixed(1))} МБ`}
                  </TableCell>
                  <TableCell sx={{ minWidth: 130, maxWidth: 280 }}>
                    <Typography variant="body2" role="status">
                      {labels[item.status]}
                    </Typography>
                    {item.status === "uploading" && <LinearProgress />}
                    {item.validationError && (
                      <Alert severity="error">{item.validationError}</Alert>
                    )}
                    <FeatureErrors error={item.error} />
                    {item.status === "error" && !item.validationError && (
                      <Alert severity="warning">
                        Ответ мог потеряться. Повтор может создать ещё один
                        ресурс. Проверьте библиотеку перед повтором.
                      </Alert>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.previewUrl &&
                      (item.file.type.startsWith("video/") ? (
                        <Box
                          component="video"
                          src={item.previewUrl}
                          controls
                          preload="metadata"
                          sx={{ width: 120, height: 72, objectFit: "contain" }}
                        />
                      ) : (
                        <Box
                          component="img"
                          src={item.previewUrl}
                          alt={item.file.name}
                          sx={{
                            width: 120,
                            height: 72,
                            objectFit: "contain",
                          }}
                        />
                      ))}
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    {item.status === "error" && !item.validationError && (
                      <Button
                        size="small"
                        disabled={disabled}
                        aria-label={`Повторить ${item.file.name}`}
                        onClick={() => {
                          if (!disabled) uploads.retry(item.localId)
                        }}
                      >
                        Повторить
                      </Button>
                    )}
                    <Button
                      size="small"
                      disabled={disabled}
                      aria-label={`Убрать ${item.file.name} из очереди`}
                      onClick={() => {
                        if (!disabled) uploads.remove(item.localId)
                      }}
                    >
                      Убрать из очереди
                    </Button>
                    <Stack direction="row">
                      <Button
                        size="small"
                        aria-label={`Загрузка ${index + 1} вверх`}
                        disabled={disabled || index === 0}
                        onClick={() => uploads.move(index, index - 1)}
                      >
                        ↑
                      </Button>
                      <Button
                        size="small"
                        aria-label={`Загрузка ${index + 1} вниз`}
                        disabled={
                          disabled || index === uploads.items.length - 1
                        }
                        onClick={() => uploads.move(index, index + 1)}
                      >
                        ↓
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  )
}
